import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { ButtonWithDropdown } from "$root/components/buttons/ButtonWithDropdown";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { type CompareDataItem, CompareOverlay } from "$root/functional-areas/compare-portfolio/CompareOverlay";
import { investmentAum, resolveCustomAttributesWithAum } from "$root/functional-areas/customAttributes/aum";
import {
	INVESTMENT_INSTRUMENT_LIMIT,
	MIN_NUMBER_OF_INSTRUMENTS,
	MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS,
} from "$root/functional-areas/const";
import { cashTickerToInstrument } from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentCompositionEditor from "$root/functional-areas/instruments-editor/instrumentEditor";
import type { EditorBannersHandle } from "$root/functional-areas/instruments-editor/banners/EditorBanners";
import type { QuantityEditorBuilderProps } from "$root/functional-areas/instruments-editor/quantity/builder";
import { useQuantityEditorBuilder } from "$root/functional-areas/instruments-editor/quantity/builder";
import { withQuantityCommitHandlers } from "$root/functional-areas/instruments-editor/quantity/columns";
import {
	compositionPassesSubmitChecks,
	resetQuantityComposition,
	saveBlockedByBanner,
	serializeComposition,
} from "$root/functional-areas/instruments-editor/quantity/editorHost";
import { withHeldIssues } from "$root/functional-areas/instruments-editor/quantity/held-issues/heldIssue";
import { confirmPastDatedSave } from "$root/functional-areas/instruments-editor/quantity/market-date/PastDateConfirmDialog";
import {
	parseMarketDateParam,
	pastDatedEditDate,
} from "$root/functional-areas/instruments-editor/quantity/market-date/marketDate";
import ExposureSankeyBlock from "$root/functional-areas/instruments-editor/InstrumentExposureSankey";
import { spawnCopyCompositionDialog } from "$root/functional-areas/instruments-editor/spawn/copy-composition";
import { spawnChooseEntitySavingModeDialog } from "$root/functional-areas/instruments-editor/spawn/saving-mode";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { customerDecimalPlaces, useUserValue } from "$root/functional-areas/user";
import { instrumentsEditorRepository } from "$root/functional-areas/instruments-editor/repository";
import { platformToast } from "$root/notification-system/toast";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQueries, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { serverTodayAsLocalDate, useSearchParams } from "$root/utils";
import type {
	CompositionTemplateEntityType,
	EditorSaveEditOrReviewRequestEditPolicyEnum,
	InvestmentSummary,
	EditorSaveEditOrReviewRequestPortfolioSavingModeEnum,
} from "@eu.mdotm.api/sphere";
import { Banner, Text } from "@mdotm/shadcn/legacy/components";
import { Button, type ButtonAsyncFn, type DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { identity } from "@mdotm/utils/functions";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Map, OrderedMap } from "immutable";
import { type MouseEventHandler, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { countInstrumentInComposition } from "../common";

function MaybeAsyncButton<T>(
	props: {
		onClickAsync: ButtonAsyncFn<T>;
		onClick: MouseEventHandler<HTMLButtonElement>;
		label: string;
		mode: "sync" | "async";
		disabled?: boolean;
	} & DataAttributesProps,
) {
	const { label, mode, onClick, onClickAsync, disabled, ...dataAttributes } = props;
	if (mode === "sync") {
		return (
			<Button {...dataAttributes} size="small" onClick={onClick} palette="primary" disabled={disabled}>
				{label}
			</Button>
		);
	}

	return (
		<Button {...dataAttributes} size="small" palette="primary" onClickAsync={onClickAsync} disabled={disabled}>
			{label}
		</Button>
	);
}

/** Initial weight-mode load: the rows go into the builder as-is. */
function resetWeightComposition(
	instrumentBuilder: QuantityEditorBuilderProps,
	params: {
		instruments: Instrument[];
		cash: Instrument | undefined;
		/** Bounds the reference date the editor's header offers here too (MDOT-25192). */
		lastTradeDate: InvestmentSummary["lastTradeDate"];
	},
): void {
	instrumentBuilder.reset(
		{
			composition: OrderedMap<string, Instrument>(params.instruments.map((x) => [x.id, x])),
			cash: params.cash,
			lastTradeDate: params.lastTradeDate,
		},
		{ disableToggleDirty: true },
	);
}

function ManualPortfolioEdit(): React.JSX.Element {
	const { uuid, workspaceUid } = useParams<{ uuid: string; workspaceUid?: string }>();
	const { copyComposition } = useSearchParams<"copyComposition">();

	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const user = useUserValue();

	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);

	const querySummary = portfolioRepository.summary.useQuery(uuid, { enhanced: false });

	const { data: summary } = querySummary;

	const instrumentBuilder = useQuantityEditorBuilder({ composition: Map() });
	const editorBannersRef = useRef<EditorBannersHandle>(null);
	const compositionMapWithoutDeletedInstruments = useMemo(
		() => instrumentBuilder.getComposition({ removeDeleted: true }),
		[instrumentBuilder],
	);

	const weightTotalPrecision = customerDecimalPlaces(user);
	const maxInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? +Infinity
		: INVESTMENT_INSTRUMENT_LIMIT;

	const minInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? MIN_NUMBER_OF_INSTRUMENTS
		: MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS;

	const hasPortfolioInComposition = useMemo(() => {
		return compositionMapWithoutDeletedInstruments.some((x) => x.proxyOverwriteType === "PORTFOLIO_MIXED");
	}, [compositionMapWithoutDeletedInstruments]);

	const onSubmit = useCallback(
		async (params: { uuid: string; portfolioSavingMode: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum }) => {
			const { portfolioSavingMode } = params;
			try {
				if (params.uuid === undefined) {
					throw Error("unable to submit a composition of undefined");
				}

				// A value typed into a quantity cell is still Cold War's to size; the save is of its answer.
				await instrumentBuilder.settleSizing();

				const nOfInstrumentsInComposition = countInstrumentInComposition(
					compositionMapWithoutDeletedInstruments,
					params.portfolioSavingMode,
				);

				if (
					!compositionPassesSubmitChecks(
						instrumentBuilder,
						nOfInstrumentsInComposition,
						{
							minInstrumentsInComposition,
							maxInstrumentsInComposition,
							weightTotalPrecision,
						},
						editorBannersRef,
					)
				) {
					return;
				}
				const composition = serializeComposition(instrumentBuilder);

				// The day the editor's header holds is the day the save is dated to (MDOT-25192), whichever
				// button wrote it — the composition on screen belongs to that day. At today it is
				// `undefined`, which is the request the plain save has always sent, and the day nothing is
				// asked about (MDOT-25046).
				const marketDate = instrumentBuilder.getMarketDate().value;
				if (!(await confirmPastDatedSave(marketDate))) {
					return;
				}

				await instrumentsEditorRepository.edit.investment(params.uuid, {
					composition,
					form: { portfolioSavingMode, editDate: pastDatedEditDate(marketDate) },
					customAttributes: await resolveCustomAttributesWithAum(params.uuid, instrumentBuilder.getAum(), "INVESTMENT"),
				});

				trackMixPanelEvent("portfolio.entityActions.editPortfolioComposition", {
					portfolio: { uuid, name: summary?.name ?? "" },
				});

				flushSync(() =>
					setPathToNotBlock([
						...(workspaceUid
							? [typedUrlForRoute("PortfolioDetailsWithWorkspace", { workspaceUid, portfolioUid: uuid })]
							: []),
						typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
					]),
				);
				if (workspaceUid) {
					push("PortfolioDetailsWithWorkspace", { workspaceUid, portfolioUid: params.uuid });
				} else {
					push("PortfolioDetails", { portfolioUid: params.uuid });
				}
			} catch (error) {
				const e = error as AxiosError<{ message?: string }>;
				const message = e.response?.data.message;

				if (message === "maxValue should be greater or equal to 0.02") {
					platformToast({
						children: "Please review your instrument, minimum weight acceptable is 0.02",
						severity: "error",
						icon: "Portfolio",
					});
				} else {
					platformToast({
						children: "Something went wrong, please try later",
						severity: "error",
						icon: "Portfolio",
					});
				}

				reportPlatformError(error, "ERROR", "portfolio", {
					message: `unable to save the edited portfolio composition (${uuid})`,
				});

				throw error;
			}
		},
		[
			compositionMapWithoutDeletedInstruments,
			instrumentBuilder,
			maxInstrumentsInComposition,
			minInstrumentsInComposition,
			push,
			summary?.name,
			uuid,
			weightTotalPrecision,
			workspaceUid,
		],
	);

	const saveAsAggregate = useCallback(async () => {
		// Checked before the dialog opens, so a save that cannot go through never asks how to save.
		if (saveBlockedByBanner(editorBannersRef)) {
			return;
		}
		const nestedPortfolios = Array.from(compositionMapWithoutDeletedInstruments.values())
			.filter((x) => x.proxyOverwriteType === "PORTFOLIO_MIXED")
			.map((x) => ({ id: x.id, name: x.name ?? "-" }));
		await spawnYesNoDialog({
			header: "Save as aggregate",
			children: (
				<>
					<Text type="Body/M/Book" as="div" className="mb-4">
						The composition will be saved as a flat list of instruments, replacing the nested portfolio structure.
					</Text>
					<Banner severity="warning" title="Nested portfolio links will be removed">
						<Text type="Body/M/Book" as="div" className="mb-2">
							Saving as aggregate will permanently break the connections with the following nested portfolios:
						</Text>
						<ul className="list-disc pl-4">
							{nestedPortfolios.map(({ id, name }) => (
								<li key={id}>{name}</li>
							))}
						</ul>
					</Banner>
				</>
			),
			async onSubmitAsync() {
				await onSubmit({
					portfolioSavingMode: "MIX_INSTRUMENTS",
					uuid: uuid,
				});
			},
		});
	}, [compositionMapWithoutDeletedInstruments, onSubmit, uuid]);

	const onSubmitEnhanceComposition = useCallback(
		async (params: {
			uuid: string;
			portfolioSavingMode: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
			savePolicy: EditorSaveEditOrReviewRequestEditPolicyEnum | "SAVE_AND_EXECUTE";
		}) => {
			const { uuid, portfolioSavingMode, savePolicy } = params;
			try {
				if (uuid === undefined) {
					throw Error("unable to submit a composition of undefined");
				}

				// A value typed into a quantity cell is still Cold War's to size; the save is of its answer.
				await instrumentBuilder.settleSizing();

				const nOfInstrumentsInComposition = countInstrumentInComposition(
					compositionMapWithoutDeletedInstruments,
					params.portfolioSavingMode,
				);

				if (
					!compositionPassesSubmitChecks(
						instrumentBuilder,
						nOfInstrumentsInComposition,
						{
							minInstrumentsInComposition,
							maxInstrumentsInComposition,
							weightTotalPrecision,
						},
						editorBannersRef,
					)
				) {
					return;
				}
				const composition = serializeComposition(instrumentBuilder);

				if (savePolicy === "SAVE_AND_EXECUTE") {
					await instrumentsEditorRepository.edit.investmentEnhancement(uuid, {
						form: { editPolicy: "SAVE_WITHOUT_ACCEPT", portfolioSavingMode },
						composition,
						customAttributes: await resolveCustomAttributesWithAum(
							uuid,
							instrumentBuilder.getAum(),
							"INVESTMENT_ENHANCEMENT",
						),
					});
					// change edit policy
					// call execution API
				} else {
					// A past day on a proposal means "accept it at that day", which is what the backend
					// enforces: `editDate` is only valid under `SAVE_AND_ACCEPT`. So the day the editor's
					// header holds dates that save alone (MDOT-25192) and is asked about there alone
					// (MDOT-25046); at today it is `undefined`, the request shape the save always sent.
					// `SAVE_WITHOUT_ACCEPT` rewrites the proposal undated whatever the header reads.
					const marketDate = savePolicy === "SAVE_AND_ACCEPT" ? instrumentBuilder.getMarketDate().value : undefined;
					if (!(await confirmPastDatedSave(marketDate))) {
						return;
					}

					await instrumentsEditorRepository.edit.investmentEnhancement(uuid, {
						form: {
							editPolicy: savePolicy,
							portfolioSavingMode,
							editDate: pastDatedEditDate(marketDate),
						},
						composition,
						customAttributes: await resolveCustomAttributesWithAum(
							uuid,
							instrumentBuilder.getAum(),
							"INVESTMENT_ENHANCEMENT",
						),
					});
				}

				trackMixPanelEvent("portfolio.entityActions.editProposalComposition", {
					portfolio: { uuid, name: summary?.name ?? "" },
				});

				if (savePolicy === "SAVE_AND_ACCEPT") {
					trackMixPanelEvent("portfolio.entityActions.acceptProposal", {
						portfolio: { uuid, name: summary?.name ?? "" },
					});
				}

				if (savePolicy === "SAVE_AND_EXECUTE") {
					trackMixPanelEvent("portfolio.entityActions.execute", {
						portfolio: { uuid, name: summary?.name ?? "" },
					});
				}

				flushSync(() =>
					setPathToNotBlock([
						...(workspaceUid
							? [typedUrlForRoute("PortfolioDetailsWithWorkspace", { workspaceUid, portfolioUid: uuid })]
							: []),
						typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
					]),
				);
				if (workspaceUid) {
					push("PortfolioDetailsWithWorkspace", { workspaceUid, portfolioUid: uuid });
				} else {
					push("PortfolioDetails", { portfolioUid: uuid });
				}
			} catch (error) {
				const e = error as AxiosError<{ message?: string }>;
				const message = e.response?.data.message;

				if (message === "maxValue should be greater or equal to 0.02") {
					platformToast({
						children: "Please review your instrument, minimum weight acceptable is 0.02",
						severity: "error",
						icon: "Portfolio",
					});
				} else {
					platformToast({
						children: "Something went wrong, please try later",
						severity: "error",
						icon: "Portfolio",
					});
				}

				reportPlatformError(error, "ERROR", "portfolio", {
					message: `unable to save the edited portfolio composition (${uuid})`,
				});

				throw error;
			}
		},
		[
			compositionMapWithoutDeletedInstruments,
			instrumentBuilder,
			maxInstrumentsInComposition,
			minInstrumentsInComposition,
			push,
			summary?.name,
			weightTotalPrecision,
			workspaceUid,
		],
	);

	const crumbs = useCrumbs();
	const workspacePath = crumbs.useWorkspacePath(workspaceUid);

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("Portfolios", summary?.uuid),
					...workspacePath,
					...crumbs.portfolioName({ ...summary, workspaceUuid: workspaceUid }),
					...crumbs.editPortfolio,
				]}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() => {
								if (workspaceUid) {
									push("PortfolioDetailsWithWorkspace", {
										workspaceUid,
										portfolioUid: uuid,
										proposal: String(summary?.status === "PROPOSAL_READY"),
									});
								} else {
									push("PortfolioDetails", {
										portfolioUid: uuid,
										proposal: String(summary?.status === "PROPOSAL_READY"),
									});
								}
							}}
							palette="tertiary"
							data-qualifier="CompositionEditor/Cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>
						{summary?.status === "PROPOSAL_READY" ? (
							<>
								<MaybeAsyncButton
									label={t("BUTTON.UPDATE_PROPOSAL")}
									mode={hasPortfolioInComposition ? "sync" : "async"}
									onClick={() =>
										// Checked before the dialog opens, so a save that cannot go through never asks how to save.
										saveBlockedByBanner(editorBannersRef) ||
										spawnChooseEntitySavingModeDialog({
											onSubmitAsync: ({ portfolioSavingMode }) =>
												onSubmitEnhanceComposition({
													portfolioSavingMode,
													uuid,
													savePolicy: "SAVE_WITHOUT_ACCEPT",
												}),
											portfolioSavingMode: "NEST_PORTFOLIOS",
										})
									}
									onClickAsync={() =>
										onSubmitEnhanceComposition({
											portfolioSavingMode: "MIX_INSTRUMENTS",
											uuid,
											savePolicy: "SAVE_WITHOUT_ACCEPT",
										})
									}
									data-qualifier="EditPortfolioComposition/UpdateProposal/Button"
								/>
								{summary.liveTrading ? (
									// <MaybeAsyncButton
									// 	label={t("BUTTON.SAVE_AND_EXECUTE")}
									// 	mode={hasPortfolioInComposition ? "sync" : "async"}
									// 	onClick={() =>
									// 		spawnChooseEntitySavingModeDialog({
									// 			onSubmitAsync: ({ portfolioSavingMode }) =>
									// 				onSubmitEnhanceComposition({
									// 					composition: serializeComposition(instrumentBuilder),
									// 					portfolioSavingMode,
									// 					uuid,
									// 					savePolicy: "SAVE_WITHOUT_ACCEPT",
									// 				}),
									// 			portfolioSavingMode: "NEST_PORTFOLIOS",
									// 		})
									// 	}
									// 	onClickAsync={() =>
									// 		onSubmitEnhanceComposition({
									// 			composition: serializeComposition(instrumentBuilder),
									// 			portfolioSavingMode: "MIX_INSTRUMENTS",
									// 			uuid,
									// 			savePolicy: "SAVE_WITHOUT_ACCEPT",
									// 		})
									// 	}
									// 	data-qualifier="EditPortfolioComposition/SaveAndAccepEnhancement/Button"
									// />
									<></>
								) : (
									<MaybeAsyncButton
										label={t("BUTTON.SAVE_AND_ACCEPT")}
										// When a portfolio is in the composition the saving mode is ambiguous, so we ask via
										// the saving-mode dialog (sync); otherwise we save & accept the aggregated
										// instruments directly (async).
										mode={hasPortfolioInComposition ? "sync" : "async"}
										onClick={() =>
											// Checked before the dialog opens, so a save that cannot go through never asks how to save.
											saveBlockedByBanner(editorBannersRef) ||
											spawnChooseEntitySavingModeDialog({
												onSubmitAsync: ({ portfolioSavingMode }) =>
													onSubmitEnhanceComposition({
														portfolioSavingMode,
														uuid,
														savePolicy: "SAVE_AND_ACCEPT",
													}),
												portfolioSavingMode: "NEST_PORTFOLIOS",
											})
										}
										onClickAsync={() =>
											onSubmitEnhanceComposition({
												portfolioSavingMode: "MIX_INSTRUMENTS",
												uuid,
												savePolicy: "SAVE_AND_ACCEPT",
											})
										}
										data-qualifier="EditPortfolioComposition/SaveAndAccepEnhancement/Button"
									/>
								)}
							</>
						) : (
							<ButtonWithDropdown
								label={t("BUTTON.SAVE")}
								palette="primary"
								size="small"
								// Default action: save with the effective date (nested when a portfolio is in
								// the composition, aggregated otherwise).
								onClickAsync={() =>
									onSubmit({
										portfolioSavingMode: hasPortfolioInComposition ? "NEST_PORTFOLIOS" : "MIX_INSTRUMENTS",
										uuid,
									})
								}
								actions={[
									// "Aggregate the instruments" (MIX_INSTRUMENTS) only differs from the default
									// nested save when a portfolio is part of the composition.
									...(hasPortfolioInComposition
										? [
												{
													label: "Save as aggregate",
													// Synchronous: opens a dialog, so the item shouldn't sit in a loading state.
													onClick: unpromisify(saveAsAggregate),
													"data-qualifier": "EditPortfolioComposition/Save/SaveAsAggregate",
												},
											]
										: []),
								]}
								dataAttributes={{
									buttonDataAttributes: { "data-qualifier": "EditPortfolioComposition/Save/Button" },
									dropdownTriggerDataAttributes: { "data-qualifier": "EditPortfolioComposition/Save/DropdownTrigger" },
								}}
							/>
						)}
					</div>
				}
			/>{" "}
			<ReactQueryWrapperBase query={querySummary}>
				{(responseSummary) => (
					<InstrumentEditorTable
						instrumentBuilder={instrumentBuilder}
						editorBannersRef={editorBannersRef}
						summary={responseSummary}
						uuid={uuid}
						maxInstrumentsInComposition={maxInstrumentsInComposition}
						minInstrumentsInComposition={minInstrumentsInComposition}
						copyComposition={copyComposition}
					/>
				)}
			</ReactQueryWrapperBase>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={instrumentBuilder.isDirty}
				pathToNotBlock={pathToNotBlock}
				// Changing the market date rewrites this page's own query string; the editor stays
				// mounted and the composition with it, so "all information will be discarded" is not
				// what is happening and must not be asked.
				allowSameRouteNavigation
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}

function InstrumentEditorTable({
	summary,
	instrumentBuilder,
	uuid,
	minInstrumentsInComposition,
	maxInstrumentsInComposition,
	copyComposition,
	editorBannersRef,
}: {
	summary: InvestmentSummary;
	instrumentBuilder: QuantityEditorBuilderProps;
	editorBannersRef: RefObject<EditorBannersHandle | null>;
	uuid: string;
	minInstrumentsInComposition: number;
	maxInstrumentsInComposition: number;
	copyComposition?: string;
}) {
	const { workspaceUid } = useParams<{ workspaceUid?: string }>();

	const { replace } = useTypedNavigation();

	// Quantity-mode gate (MDOT-24700): an accepted quantity portfolio's plain summary carries
	// `quantityMode`; while a proposal is pending only the ENHANCED summary does — the plain one
	// says false. Same source the read-only proposal view gates on.
	const isProposal = summary.status === "PROPOSAL_READY";
	const queryEnhancedSummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(uuid, { enhanced: true }),
		enabled: isProposal,
	});
	// Both branches coalesce to a real boolean: the flag gates a query's `enabled`, where
	// `undefined` means ENABLED, so an absent `quantityMode` would fire that query on every
	// weight-mode portfolio.
	const quantityModeEnabled = isProposal
		? (queryEnhancedSummary.data?.quantityMode ?? false)
		: (summary.quantityMode ?? false);

	// The quantity editor's fixed budget (MDOT-24700): the AUM the portfolio carries on the
	// system-managed AUM custom attribute, not a number derived from the composition. A pending
	// proposal keeps its own value, so the read targets the same entity the editor saves to.
	// Enabled only in quantity mode — weight mode never spends the two calls behind it.
	const queryQuantityAum = useQuery({
		...investmentAum.getQueryConfig(uuid, isProposal ? "INVESTMENT_ENHANCEMENT" : "INVESTMENT"),
		enabled: quantityModeEnabled,
	});

	const [expandExposure, setExpandExposure] = useState(false);

	const queryEditComposition = instrumentsRepository.editComposition.useQuery(
		uuid,
		isProposal ? "INVESTMENT_ENHANCEMENT" : "INVESTMENT",
	);

	const queryUserInstrumentMap = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryEditComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryEditComposition.data != null,
	});

	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryEditComposition.data?.composition ?? []),
		enabled: queryEditComposition.data != null,
	});

	// Everything the editor needs before its initial builder reset, joined as one query.
	const queryEditorInitialData = useMappedQueries(
		identity,
		[],
		// The enhanced summary joins the group so the reset below never races the quantity
		// gate — a proposal's composition only reaches `onSuccess` once `quantityModeEnabled`
		// is settled. The AUM joins for the same reason: in quantity mode the reset needs the
		// budget, and it enters the join on the same render its `enabled` turns true, so
		// `onSuccess` waits for it. Each is disabled outside the case it serves, where a
		// disabled query would never resolve, so it must stay out of the join there.
		[
			queryEditComposition,
			queryUserInstrumentMap,
			queryInvestmentsRichInfo,
			...(isProposal ? [queryEnhancedSummary] : []),
			...(quantityModeEnabled ? [queryQuantityAum] : []),
		],
	);

	useQueryReactor(queryEditorInitialData, {
		onSuccess([compositionWithCash, userInstrumentsMap, investmentsRichInfo]) {
			const instruments = createInstrumentList({
				composition: compositionWithCash.composition ?? [],
				userInstrumentsMap,
				investmentData: investmentsRichInfo,
			});
			const cash = compositionWithCash.cashTicker ? cashTickerToInstrument(compositionWithCash.cashTicker) : undefined;

			if (quantityModeEnabled) {
				resetQuantityComposition(instrumentBuilder, {
					instruments: withHeldIssues(instruments, compositionWithCash.composition ?? []),
					cash,
					cashTickerName: compositionWithCash.cashTicker?.ticker,
					// Read off the query rather than the joined tuple: the tuple's tail is conditional
					// (enhanced summary, AUM), so its positions do not line up across the two modes.
					aum: queryQuantityAum.data ?? null,
					currency: summary.baseCurrency,
					lastTradeDate: summary.lastTradeDate,
					investmentUuid: uuid,
				});
				// A deep-linked `?marketDate` is worth nothing if the grid still shows today's closes under
				// it, so the day it names is priced right after the reset (MDOT-25191). Re-validated against
				// the lower bound the summary has only now supplied — a day outside the window is about to be
				// stripped from the URL, and pricing at it would answer nothing but errors.
				const marketDate = parseMarketDateParam(instrumentBuilder.getMarketDate().value, {
					lastTradeDate: summary.lastTradeDate,
				});
				if (marketDate && marketDate !== serverTodayAsLocalDate()) {
					void instrumentBuilder.repriceAt(marketDate);
				}
			} else {
				resetWeightComposition(instrumentBuilder, { instruments, cash, lastTradeDate: summary.lastTradeDate });
			}
			const someInvestmentInComposition = instruments.some((x) => x.instrumentType === "INVESTMENT");
			if (someInvestmentInComposition) {
				setExpandExposure(true);
			}
		},
	});

	const comparedInvestments = useMemo((): CompareDataItem[] => {
		const investmentsCompared = instrumentBuilder
			.getComposition()
			.filter((x) => instrumentBuilder.getComparedInstrument(x.id) !== undefined)
			.values();

		return Array.from(investmentsCompared).map((instrument) => ({
			id: instrument.id,
			composition:
				instrument.macroAssetClassExposure?.map((x) => ({
					quality: x.firstQualityLevel,
					weight: x.weight,
				})) ?? [],
			numberOfInstrument: instrument.nofInstruments ?? 0,
			portfolioName: instrument.name ?? "-",
			uuid: instrument.investmentUuid,
			note: instrument.lastActionNote,
			action: instrument.action,
		}));
	}, [instrumentBuilder]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <not needed instrumentBuilder.reset>
	useEffect(() => {
		if (copyComposition) {
			spawnCopyCompositionDialog({
				entity: "INVESTMENT",
				uuid,
				initialSidePanelFilters: {
					search: "",
					filters: [
						{
							key: "TYPE",
							relation: "EQUAL",
							type: "FINITE_SET_TYPE",
							value: ["REFERENCE_INVESTMENT", "INVESTMENT"] satisfies CompositionTemplateEntityType[],
						},
					],
				},
				onSubmitAsync: async (data) => {
					await runWithErrorReporting(
						async () => {
							const copied = await instrumentsRepository.copyComposition.fetchQuery(data.uuid, data.type);
							const { cashTicker } = copied;
							const instruments = withHeldIssues(copied.instruments, copied.composition);

							const marketDate = instrumentBuilder.getMarketDate().value;

							// A copy replaces the composition and the quantity state with it, but not the portfolio
							// being edited — so its last trade date rides through, and the reference date keeps the
							// window it was bounded by.
							instrumentBuilder.reset(({ cash, lastTradeDate }) => ({
								composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
								cash: cashTicker ? cashTickerToInstrument(cashTicker) : cash,
								lastTradeDate,
							}));

							const someInvestmentInComposition = instruments.find((x) => x.instrumentType === "INVESTMENT");
							if (someInvestmentInComposition) {
								setExpandExposure(true);
							}
							// The rewrite drops `copyComposition` by rebuilding the URL, and with it every other
							// param — so the market date has to be named again or a copy silently resets it.
							if (workspaceUid) {
								replace("Portfolios/ManualEditPortfolioWithWorkspace", { workspaceUid, uuid, marketDate });
							} else {
								replace("Portfolios/ManualEditPortfolio", { uuid, marketDate });
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "attempt to load entity template",
								payload: JSON.stringify(data),
							},
						},
					);
				},
			});
		}
	}, [replace, copyComposition, uuid, workspaceUid]);
	const classifications = useAtomValue(userClassifications$) ?? [];

	// The column set for the current surface — exactly one of the four variants
	// (proposal/plain × quantity/weight) is built. The quantity variants (MDOT-24700) are
	// render-only — quantity, price and market value are read straight off the rows, which
	// the edit engine keeps up to date (an AUM change rewrites every row's derived fields,
	// so no column re-derivation is needed); the plain-edit one carries no proposal context
	// (previous weight / delta belong to the proposal review only), matching the entity
	// split of the persisted column-state key in the editor table. The commit handlers
	// capture `instrumentBuilder` deliberately without depending on it: the wrapper's
	// methods are staleness-safe, and re-deriving the column defs on every edit would make
	// AG Grid reprocess its columns mid-editing.
	// biome-ignore lint/correctness/useExhaustiveDependencies: instrumentBuilder's methods are staleness-safe (chained-ref design) — see comment above
	const columns = useMemo(() => {
		if (isProposal) {
			return quantityModeEnabled
				? withQuantityCommitHandlers(
						columnsDefinition.instrumentQuantityProposalComposition(classifications, "edit", {
							proposalDate: summary.modificationDate,
							currency: summary.baseCurrency,
							summary,
						}),
						instrumentBuilder,
					)
				: columnsDefinition.instrumentProposalComposition(classifications, "edit", {
						proposalDate: summary.modificationDate,
					});
		}
		return quantityModeEnabled
			? withQuantityCommitHandlers(
					columnsDefinition.instrumentQuantityComposition(classifications, "edit", {
						currency: summary.baseCurrency,
					}),
					instrumentBuilder,
				)
			: columnsDefinition.instrumentComposition(classifications);
	}, [classifications, isProposal, quantityModeEnabled, summary]);
	return (
		<ReactQueryWrapperBase query={queryEditorInitialData}>
			{() => (
				<>
					<div className="min-h-0 grow flex">
						<InstrumentCompositionEditor
							entity={isProposal ? "INVESTMENT_ENHANCEMENT" : "INVESTMENT"}
							mode="edit"
							uuid={uuid}
							instrumentBuilder={instrumentBuilder}
							editorBannersRef={editorBannersRef}
							minThreshold={minInstrumentsInComposition}
							limit={maxInstrumentsInComposition}
							columns={columns}
							compositionMode={quantityModeEnabled ? "quantity" : "weight"}
							marketDate={instrumentBuilder.getMarketDate()}
						/>
						<ExposureSankeyBlock
							expand={expandExposure}
							onExpandChange={setExpandExposure}
							instrumentBuilder={instrumentBuilder}
						/>
					</div>

					<CompareOverlay
						show={comparedInvestments.length > 0}
						onClose={() => instrumentBuilder.clearComparedInstruments()}
						onRemove={(id) => instrumentBuilder.deleteComparedInstruments(id)}
						compareData={comparedInvestments}
					/>
				</>
			)}
		</ReactQueryWrapperBase>
	);
}

export default ManualPortfolioEdit;
